'use client'

import { useCallback, useEffect, useReducer, useRef } from 'react'

import { createLogger } from '@/lib/log/client'
import type { ReleaseReviewsData, ReviewWithAvatar } from '@/lib/supabase/get-release-reviews'
import { isLocalStorageAvailable } from '@/lib/utils'

import { mergeReviews } from './merge-reviews'

const log = createLogger('use-reviews')

const PAGE_SIZE = 3

// localStorage

interface StoredReview {
  score: number
  reviewId: number | null
}

const getStorageKey = (releaseId: number) => `ht-review-${releaseId}`

const loadStoredReview = (releaseId: number): StoredReview | null => {
  if (!isLocalStorageAvailable()) {
    return null
  }
  try {
    const raw = localStorage.getItem(getStorageKey(releaseId))
    if (!raw) {
      return null
    }
    return JSON.parse(raw) as StoredReview
  } catch {
    return null
  }
}

const saveStoredReview = (releaseId: number, data: StoredReview) => {
  if (!isLocalStorageAvailable()) {
    return
  }
  try {
    localStorage.setItem(getStorageKey(releaseId), JSON.stringify(data))
  } catch {
    // ignore
  }
}

// State

enum ActionType {
  HydrateStoredReview = 'HYDRATE_STORED_REVIEW',
  SetModelFilter = 'SET_MODEL_FILTER',
  AppendReviews = 'APPEND_REVIEWS',
  ReplaceReviews = 'REPLACE_REVIEWS',
  SetLoading = 'SET_LOADING',
  SetUserScore = 'SET_USER_SCORE',
  OpenModal = 'OPEN_MODAL',
  CloseModal = 'CLOSE_MODAL',
  ReviewSubmitted = 'REVIEW_SUBMITTED',
}

/** One page from `/api/reviews`, with the summary over every matching review. */
interface ReviewsPage {
  reviews: ReviewWithAvatar[]
  total: number
  averageScore: number | null
  distribution?: number[]
}

interface ReviewsState {
  reviews: ReviewWithAvatar[]
  totalCount: number
  averageScore: number | null
  /** Review counts by whole star, index 0 = 1 star … index 4 = 5 stars. */
  distribution?: number[]
  selectedModelId: number | null
  /** The API returned a short page: nothing older is left to load, even if
   *  `totalCount` (which includes reviews newer than the first page) says so. */
  exhausted: boolean
  loading: boolean
  userScore: number
  existingReviewId: number | null
  isModalOpen: boolean
}

type ReviewsAction =
  | { type: ActionType.HydrateStoredReview; stored: StoredReview | null }
  | { type: ActionType.SetModelFilter; modelId: number | null }
  | { type: ActionType.AppendReviews; page: ReviewsPage }
  | { type: ActionType.ReplaceReviews; page: ReviewsPage }
  | { type: ActionType.SetLoading; loading: boolean }
  | { type: ActionType.SetUserScore; score: number }
  | { type: ActionType.OpenModal }
  | { type: ActionType.CloseModal }
  | { type: ActionType.ReviewSubmitted; reviewId: number; score: number }

const reducer = (state: ReviewsState, action: ReviewsAction): ReviewsState => {
  switch (action.type) {
    case ActionType.HydrateStoredReview:
      return {
        ...state,
        userScore: action.stored?.score ?? 0,
        existingReviewId: action.stored?.reviewId ?? null,
      }
    case ActionType.SetModelFilter:
      // The last numbers and cards stay up (dimmed) until the new page lands,
      // so the section doesn't collapse and re-grow on every filter change.
      return {
        ...state,
        selectedModelId: action.modelId,
        exhausted: false,
        loading: true,
      }
    case ActionType.AppendReviews:
      return {
        ...state,
        reviews: mergeReviews(state.reviews, action.page.reviews),
        totalCount: action.page.total,
        averageScore: action.page.averageScore,
        distribution: action.page.distribution,
        exhausted: action.page.reviews.length < PAGE_SIZE,
        loading: false,
      }
    case ActionType.ReplaceReviews:
      return {
        ...state,
        reviews: action.page.reviews,
        totalCount: action.page.total,
        averageScore: action.page.averageScore,
        distribution: action.page.distribution,
        exhausted: action.page.reviews.length < PAGE_SIZE,
        loading: false,
      }
    case ActionType.SetLoading:
      return { ...state, loading: action.loading }
    case ActionType.SetUserScore:
      return { ...state, userScore: action.score }
    case ActionType.OpenModal:
      return { ...state, isModalOpen: true }
    case ActionType.CloseModal:
      return { ...state, isModalOpen: false }
    case ActionType.ReviewSubmitted:
      return {
        ...state,
        existingReviewId: action.reviewId,
        userScore: action.score,
      }
    default:
      return state
  }
}

// Hook

interface UseReviewsParams {
  releaseId: number
  /** The first page, straight from the cached product render. */
  initialData: ReleaseReviewsData
}

export const useReviews = ({ releaseId, initialData }: UseReviewsParams) => {
  const [state, dispatch] = useReducer(reducer, {
    reviews: initialData.reviews,
    totalCount: initialData.totalCount,
    averageScore: initialData.averageScore,
    distribution: initialData.distribution,
    selectedModelId: null,
    exhausted: false,
    loading: false,
    userScore: 0,
    existingReviewId: null,
    isModalOpen: false,
  })
  // Two quick filter changes race their responses; only the latest counts.
  const latestRequest = useRef(0)

  useEffect(() => {
    dispatch({ type: ActionType.HydrateStoredReview, stored: loadStoredReview(releaseId) })
  }, [releaseId])

  // Fetch reviews from API (for filter changes and pagination). `after` is the
  // last review on screen: the API pages by (created_at, id) keyset rather than
  // OFFSET, so a review approved after the cached first page rendered can't
  // shift the window and repeat a card.
  const fetchReviews = useCallback(
    async (modelId: number | null, after: ReviewWithAvatar | null, replace: boolean) => {
      const requestId = ++latestRequest.current
      dispatch({ type: ActionType.SetLoading, loading: true })
      try {
        const params = new URLSearchParams({
          release_id: String(releaseId),
          limit: String(PAGE_SIZE),
        })
        if (after) {
          params.set('before_created_at', after.createdAt)
          params.set('before_id', String(after.id))
        }
        if (modelId != null) {
          params.set('model_id', String(modelId))
        }
        const res = await fetch(`/api/reviews?${params}`)
        if (!res.ok) {
          throw new Error('Failed to fetch reviews')
        }
        const page = (await res.json()) as ReviewsPage
        if (requestId !== latestRequest.current) {
          return
        }
        dispatch({ type: replace ? ActionType.ReplaceReviews : ActionType.AppendReviews, page })
      } catch (error) {
        if (requestId !== latestRequest.current) {
          return
        }
        log.error('Failed to fetch reviews:', error)
        dispatch({ type: ActionType.SetLoading, loading: false })
      }
    },
    [releaseId]
  )

  // Switch the list (and the summary) to one model, or back to all of them.
  const setModelFilter = useCallback(
    (modelId: number | null) => {
      if (modelId === state.selectedModelId) {
        return
      }
      dispatch({ type: ActionType.SetModelFilter, modelId })
      void fetchReviews(modelId, null, true)
    },
    [state.selectedModelId, fetchReviews]
  )

  const loadMore = useCallback(() => {
    const last = state.reviews[state.reviews.length - 1] ?? null
    void fetchReviews(state.selectedModelId, last, false)
  }, [fetchReviews, state.selectedModelId, state.reviews])

  const setUserScore = useCallback((score: number) => {
    dispatch({ type: ActionType.SetUserScore, score })
  }, [])

  const openModal = useCallback(() => {
    dispatch({ type: ActionType.OpenModal })
  }, [])

  const closeModal = useCallback(() => {
    dispatch({ type: ActionType.CloseModal })
  }, [])

  const onReviewSubmitted = useCallback(
    (reviewId: number, score: number) => {
      dispatch({ type: ActionType.ReviewSubmitted, reviewId, score })
      saveStoredReview(releaseId, { score, reviewId })
    },
    [releaseId]
  )

  const hasMore = !state.exhausted && state.reviews.length < state.totalCount

  return {
    ...state,
    hasMore,
    setModelFilter,
    loadMore,
    setUserScore,
    openModal,
    closeModal,
    onReviewSubmitted,
  }
}
